import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Play, Calendar, Star, Download } from 'lucide-react';

const NotificationCenter = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([
    {
      id: 1,
      type: 'new_release',
      title: 'Nuevo Estreno',
      message: 'Ya disponible: "El Último Horizonte"',
      movie: { id: 1, title: 'El Último Horizonte', image: '...' },
      timestamp: new Date(Date.now() - 1000 * 60 * 30), // 30 minutos ago
      read: false
    },
    {
      id: 2,
      type: 'continue_watching',
      title: 'Continúa Viendo',
      message: 'Tienes 3 películas sin terminar',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 horas ago
      read: false
    },
    {
      id: 3,
      type: 'download_complete',
      title: 'Descarga Completada',
      message: '"Furia Máxima" está lista para ver offline',
      movie: { id: 2, title: 'Furia Máxima' },
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5), // 5 horas ago
      read: true
    }
  ]);

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(notif => ({ ...notif, read: true })));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: -20 }}
            className="absolute top-16 right-4 w-96 bg-gray-900 rounded-xl border border-gray-700/50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-700/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-red-400" />
                  <h3 className="text-lg font-semibold text-white">Notificaciones</h3>
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Marcar todo como leído
                    </button>
                  )}
                  <motion.button
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                    whileHover={{ scale: 1.1 }}
                  >
                    <X className="w-4 h-4" />
                  </motion.button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No hay notificaciones</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-700/50">
                  {notifications.map((notification, index) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`p-4 hover:bg-gray-800/50 transition-colors ${
                        !notification.read ? 'bg-blue-500/5' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className={`p-2 rounded-lg ${
                          notification.type === 'new_release' ? 'bg-green-500/20 text-green-400' :
                          notification.type === 'continue_watching' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-purple-500/20 text-purple-400'
                        }`}>
                          {notification.type === 'new_release' && <Star className="w-4 h-4" />}
                          {notification.type === 'continue_watching' && <Play className="w-4 h-4" />}
                          {notification.type === 'download_complete' && <Download className="w-4 h-4" />}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <h4 className="text-white font-semibold text-sm">
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                              >
                                Marcar como leído
                              </button>
                            )}
                          </div>
                          <p className="text-gray-300 text-sm mt-1">
                            {notification.message}
                          </p>
                          <p className="text-gray-500 text-xs mt-2">
                            {notification.timestamp.toLocaleTimeString('es-ES', { 
                              hour: '2-digit', 
                              minute: '2-digit' 
                            })}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationCenter;